import { Download, X } from 'lucide-react';
import { useRegisterSW } from 'virtual:pwa-register/react';

/**
 * "New version available" prompt.
 *
 * Listens for a `waiting` service worker (Workbox `autoUpdate` puts the
 * fresh SW in waiting state until all tabs close — without this banner
 * reps had to kill-and-relaunch the PWA after every deploy to pick up
 * changes; see handover doc).
 *
 * When `needRefresh === true`, renders a bottom snackbar with two
 * actions:
 *   - Update now → calls `updateServiceWorker(true)` which triggers
 *     SKIP_WAITING + reload, swapping in the new assets immediately
 *   - Dismiss    → just hides the banner; the SW stays waiting and the
 *                  banner will re-show on the next route change or
 *                  reload check
 *
 * Mounted at the top of the React tree (in main.jsx) so it overlays
 * every route. Z-index above ConfirmDialog (z-50) so it never gets
 * trapped behind a modal — but the banner is non-blocking so the user
 * can still finish whatever they were doing before refreshing.
 */
export default function UpdateBanner() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegisterError(err) {
      // Don't crash the app if SW registration fails — the PWA still
      // works, just without offline shell + auto-update. Log for
      // diagnostics.
      // eslint-disable-next-line no-console
      console.warn('Service worker registration failed', err);
    },
  });

  if (!needRefresh) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      // Bottom-anchored snackbar — doesn't fight with the amber
      // PageHeader at the top, mirrors Android's "App update available"
      // pattern. Padded to clear the iOS home indicator.
      className="fixed left-3 right-3 z-[60] flex items-center gap-3 rounded-card bg-gray-900 text-white shadow-2xl px-4 py-3"
      style={{ bottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
    >
      <Download className="h-5 w-5 shrink-0 text-amber-300" />
      <div className="min-w-0 flex-1">
        <p className="text-small font-semibold">New version available</p>
        <p className="text-caption text-gray-300">Refresh to load the latest changes.</p>
      </div>
      <button
        type="button"
        onClick={() => updateServiceWorker(true)}
        className="min-h-touch px-3 rounded-card bg-amber-500 active:bg-amber-600 text-white text-small font-semibold"
      >
        Update
      </button>
      <button
        type="button"
        onClick={() => setNeedRefresh(false)}
        aria-label="Dismiss"
        className="min-h-touch min-w-touch flex items-center justify-center text-gray-400 active:text-white -mr-1"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
